import { Check, Filter, X } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { DateRangePicker } from "@/components/ui/date-picker"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  activeFilterCount,
  isFilterActive,
  type BooleanFilterDef,
  type DateRangeFilterDef,
  type FilterDef,
  type FilterOption,
  type FilterValue,
  type FilterValues,
  type NumberRangeFilterDef,
  type RangeValue,
  type SelectFilterDef,
  type TextFilterDef,
} from "@/lib/table"
import { cn } from "@/lib/utils"

export interface FilterButtonProps {
  schema: FilterDef[]
  values: FilterValues
  onChange: (values: FilterValues) => void
  onClearAll: () => void
  /** Optional label on the trigger. Defaults to "Filters". */
  label?: string
  className?: string
}

export function FilterButton({
  schema,
  values,
  onChange,
  onClearAll,
  label = "Filters",
  className,
}: FilterButtonProps): JSX.Element {
  const [open, setOpen] = React.useState(false)
  const count = activeFilterCount(values)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="default"
          size="sm"
          leadingIcon={<Filter />}
          aria-label={count > 0 ? `${label}, ${count} active` : label}
          className={className}
        >
          {label}
          {count > 0 && (
            <span
              aria-hidden="true"
              className="ml-1 grid h-4 min-w-[16px] place-items-center rounded-full bg-brand px-1 font-id text-[10px] font-semibold !text-white"
            >
              {count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-[380px] p-0">
        <FilterPanel
          schema={schema}
          values={values}
          onChange={onChange}
          onClearAll={onClearAll}
          onClose={() => {
            setOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

export interface FilterPanelProps {
  schema: FilterDef[]
  values: FilterValues
  onChange: (values: FilterValues) => void
  onClearAll: () => void
  onClose: () => void
}

export function FilterPanel({
  schema,
  values,
  onChange,
  onClearAll,
  onClose,
}: FilterPanelProps): JSX.Element {
  const [draft, setDraft] = React.useState<FilterValues>(values)

  // Keep draft in sync when the parent's values change while panel is open
  // (e.g. a chip was removed from outside).
  React.useEffect(() => {
    setDraft(values)
  }, [values])

  const setDraftValue = React.useCallback((id: string, v: FilterValue) => {
    setDraft((prev) => ({ ...prev, [id]: v }))
  }, [])

  const apply = () => {
    onChange(draft)
    onClose()
  }

  const clear = () => {
    setDraft({})
    onClearAll()
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(values)

  return (
    <div className="flex max-h-[70vh] flex-col">
      <div className="flex items-center justify-between border-b border-border px-3.5 py-2.5">
        <div className="font-id text-eyebrow uppercase tracking-[0.14em] text-text-3">Filters</div>
        <button
          type="button"
          onClick={clear}
          className="text-caption text-text-2 underline underline-offset-2 hover:text-text"
        >
          Clear all
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3.5 py-3">
        <div className="space-y-3.5">
          {schema.map((def) => (
            <FilterField
              key={def.id}
              def={def}
              value={draft[def.id]}
              onChange={(v) => {
                setDraftValue(def.id, v)
              }}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-border px-3.5 py-2.5">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={apply} disabled={!dirty}>
          Apply
        </Button>
      </div>
    </div>
  )
}

function FilterField({
  def,
  value,
  onChange,
}: {
  def: FilterDef
  value: FilterValue
  onChange: (v: FilterValue) => void
}): JSX.Element {
  return (
    <div>
      <label
        htmlFor={`filter-${def.id}`}
        className="mb-1.5 block text-caption font-medium text-text-2"
      >
        {def.label}
      </label>
      {def.kind === "text" && (
        <TextField def={def} value={value as string | undefined} onChange={onChange} />
      )}
      {def.kind === "select" && (
        <SelectField def={def} value={value as string | string[] | undefined} onChange={onChange} />
      )}
      {def.kind === "boolean" && (
        <BooleanField def={def} value={value as boolean | undefined} onChange={onChange} />
      )}
      {def.kind === "date-range" && (
        <DateRangeField
          def={def}
          value={value as RangeValue<string> | undefined}
          onChange={onChange}
        />
      )}
      {def.kind === "number-range" && (
        <NumberRangeField
          def={def}
          value={value as RangeValue<number> | undefined}
          onChange={onChange}
        />
      )}
    </div>
  )
}

function TextField({
  def,
  value,
  onChange,
}: {
  def: TextFilterDef
  value: string | undefined
  onChange: (v: FilterValue) => void
}): JSX.Element {
  return (
    <Input
      id={`filter-${def.id}`}
      value={value ?? ""}
      placeholder={def.placeholder}
      onChange={(e) => {
        onChange(e.currentTarget.value)
      }}
      clearable
      onClear={() => {
        onChange("")
      }}
    />
  )
}

function SelectField({
  def,
  value,
  onChange,
}: {
  def: SelectFilterDef
  value: string | string[] | undefined
  onChange: (v: FilterValue) => void
}): JSX.Element {
  if (def.multi) {
    const selected = new Set(Array.isArray(value) ? value : [])
    const toggle = (v: string) => {
      const next = new Set(selected)
      next.has(v) ? next.delete(v) : next.add(v)
      onChange(Array.from(next))
    }
    return (
      <div className="max-h-44 overflow-y-auto rounded-md border border-border bg-surface">
        {def.options.map((opt) => {
          const isOn = selected.has(opt.value)
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                toggle(opt.value)
              }}
              aria-pressed={isOn}
              className={cn(
                "flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-body-sm",
                "transition-colors duration-120 hover:bg-brand-tint/60",
                isOn ? "text-text" : "text-text-2",
              )}
            >
              <span
                className={cn(
                  "grid size-4 place-items-center rounded-sm border",
                  isOn ? "border-brand bg-brand !text-white" : "border-border bg-surface",
                )}
              >
                {isOn && <Check className="size-3" aria-hidden="true" />}
              </span>
              <span className="min-w-0 flex-1 truncate">{opt.label}</span>
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <select
      id={`filter-${def.id}`}
      value={typeof value === "string" ? value : ""}
      onChange={(e) => {
        onChange(e.currentTarget.value || null)
      }}
      className={cn(
        "h-9 w-full rounded-md border border-border bg-surface px-2.5 text-body text-text",
        "transition-colors duration-120 hover:border-border-strong",
        "focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30",
      )}
    >
      <option value="">{def.placeholder ?? "Any"}</option>
      {def.options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  )
}

function BooleanField({
  def,
  value,
  onChange,
}: {
  def: BooleanFilterDef
  value: boolean | undefined
  onChange: (v: FilterValue) => void
}): JSX.Element {
  const on = Boolean(value)
  return (
    <label className="flex cursor-pointer items-start gap-2.5">
      <input
        id={`filter-${def.id}`}
        type="checkbox"
        checked={on}
        onChange={(e) => {
          onChange(e.currentTarget.checked)
        }}
        className="mt-0.5 size-4 shrink-0 accent-[color:var(--brand)]"
      />
      <span className="min-w-0 flex-1">
        <span className="block text-body-sm text-text">{def.label}</span>
        {def.hint && <span className="block text-caption text-text-3">{def.hint}</span>}
      </span>
    </label>
  )
}

function DateRangeField({
  def,
  value,
  onChange,
}: {
  def: DateRangeFilterDef
  value: RangeValue<string> | undefined
  onChange: (v: FilterValue) => void
}): JSX.Element {
  return (
    <DateRangePicker
      from={value?.from ?? null}
      to={value?.to ?? null}
      onChange={(v) => {
        onChange({ from: v.from, to: v.to })
      }}
      fromLabel={`${def.label} from`}
      toLabel={`${def.label} to`}
    />
  )
}

function NumberRangeField({
  def,
  value,
  onChange,
}: {
  def: NumberRangeFilterDef
  value: RangeValue<number> | undefined
  onChange: (v: FilterValue) => void
}): JSX.Element {
  const from = value?.from ?? null
  const to = value?.to ?? null
  const update = (side: "from" | "to", v: string) => {
    const parsed = v === "" ? null : Number(v)
    if (parsed !== null && Number.isNaN(parsed)) return
    onChange({
      from: side === "from" ? parsed : from,
      to: side === "to" ? parsed : to,
    })
  }
  return (
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <Input
          id={`filter-${def.id}-min`}
          type="number"
          inputMode="numeric"
          min={def.min}
          max={def.max}
          value={from ?? ""}
          onChange={(e) => {
            update("from", e.currentTarget.value)
          }}
          aria-label={`${def.label} minimum`}
          placeholder="Min"
        />
      </div>
      <span className="text-text-3">–</span>
      <div className="min-w-0 flex-1">
        <Input
          id={`filter-${def.id}-max`}
          type="number"
          inputMode="numeric"
          min={def.min}
          max={def.max}
          value={to ?? ""}
          onChange={(e) => {
            update("to", e.currentTarget.value)
          }}
          aria-label={`${def.label} maximum`}
          placeholder="Max"
        />
      </div>
    </div>
  )
}

export interface ActiveFilterChipsProps {
  schema: FilterDef[]
  values: FilterValues
  onRemove: (id: string) => void
  onClearAll: () => void
  className?: string
}

export function ActiveFilterChips({
  schema,
  values,
  onRemove,
  onClearAll,
  className,
}: ActiveFilterChipsProps): JSX.Element | null {
  const chips = schema
    .filter((def) => isFilterActive(values[def.id]))
    .map((def) => ({ def, label: formatChipLabel(def, values[def.id]) }))

  if (chips.length === 0) return null

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {chips.map(({ def, label }) => (
        <span
          key={def.id}
          className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-sunken py-0.5 pl-2.5 pr-1 text-caption text-text-2"
        >
          <span className="text-text-3">{def.label}:</span>
          <span className="text-text">{label}</span>
          <button
            type="button"
            onClick={() => {
              onRemove(def.id)
            }}
            aria-label={`Remove ${def.label} filter`}
            className="grid size-4 place-items-center rounded-full text-text-3 hover:bg-border hover:text-text"
          >
            <X className="size-3" aria-hidden="true" />
          </button>
        </span>
      ))}
      {chips.length > 1 && (
        <button
          type="button"
          onClick={onClearAll}
          className="text-caption text-text-2 underline underline-offset-2 hover:text-text"
        >
          Clear all
        </button>
      )}
    </div>
  )
}

function formatChipLabel(def: FilterDef, value: FilterValue): string {
  switch (def.kind) {
    case "text":
      return typeof value === "string" ? value : ""
    case "select": {
      const options = def.options
      if (def.multi && Array.isArray(value)) {
        if (value.length <= 2) return value.map((v) => labelFor(options, v)).join(", ")
        return `${labelFor(options, value[0]!)} +${value.length - 1}`
      }
      return labelFor(options, value as string)
    }
    case "boolean":
      return "Yes"
    case "date-range": {
      const v = value as RangeValue<string>
      return formatRange(v.from, v.to)
    }
    case "number-range": {
      const v = value as RangeValue<number>
      return formatRange(v.from, v.to)
    }
  }
}

function labelFor(options: FilterOption[], v: string): string {
  return options.find((o) => o.value === v)?.label ?? v
}

function formatRange<T>(from: T | null, to: T | null): string {
  if (from != null && to != null) return `${from} – ${to}`
  if (from != null) return `≥ ${from}`
  if (to != null) return `≤ ${to}`
  return ""
}
